import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import Header from './components/Header25';
import Footer from './components/Footer25';

const PrivacyPolicy25 = () => {
    const navigate = useNavigate();

    const sections = [
        {
            title: 'Information We Collect',
            content: 'We collect information you provide directly, including name, email address, phone number, shipping address, and payment details when you create an account, make a purchase, or contact us. We also automatically collect certain technical information such as IP address, browser type, device information, and browsing behavior through cookies and similar technologies.'
        },
        {
            title: 'How We Use Your Information',
            items: [
                'Process and fulfill your orders, including shipping and delivery',
                'Send order confirmations, shipping updates, and delivery notifications',
                'Provide customer support and respond to your inquiries',
                'Personalize your shopping experience and recommend products',
                'Send promotional emails and newsletters (with your consent)',
                'Improve our website, products, and services',
                'Prevent fraud and ensure security of transactions',
                'Comply with legal obligations',
            ]
        },
        {
            title: 'Data Protection & Security',
            content: 'We implement industry-standard security measures to protect your personal data. All payment transactions are encrypted using SSL technology. We do not store your complete credit card information on our servers. Access to personal data is restricted to authorized personnel only. We regularly review and update our security practices to ensure the safety of your information.'
        },
        {
            title: 'Cookies & Tracking',
            content: 'We use cookies and similar tracking technologies to enhance your browsing experience. Essential cookies are required for site functionality. Analytics cookies help us understand how visitors interact with our website. Marketing cookies may be used to deliver relevant advertisements. You can manage cookie preferences through your browser settings.'
        },
        {
            title: 'Sharing Your Information',
            content: 'We do not sell your personal information to third parties. We may share your data with trusted service providers who assist in operating our website, processing payments, and delivering orders. These partners are contractually obligated to protect your information. We may also disclose information when required by law or to protect our rights.'
        },
        {
            title: 'Your Rights',
            items: [
                'Access and review the personal information we hold about you',
                'Request correction of inaccurate or incomplete data',
                'Request deletion of your personal data (subject to legal obligations)',
                'Opt out of marketing communications at any time',
                'Withdraw consent for data processing',
                'Lodge a complaint with a data protection authority',
            ]
        },
        {
            title: 'Data Retention',
            content: 'We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, or as required by law. Order-related data is retained for a minimum of 8 years for tax and accounting purposes. You may request deletion of your account at any time, though certain information may be retained as required by law.'
        },
        {
            title: 'Third-Party Links',
            content: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party websites you visit.'
        },
        {
            title: 'Children\'s Privacy',
            content: 'Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.'
        },
        {
            title: 'Updates to This Policy',
            content: 'We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. The updated policy will be posted on this page with a revised "last updated" date. We encourage you to review this policy regularly.'
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
                .sahaj-privacy * { box-sizing: border-box; }
                .sahaj-privacy { font-family: 'IBM Plex Sans', sans-serif; background: #FFFFFF; color: #111111; }
                .sahaj-privacy-heading { font-family: 'Space Grotesk', sans-serif; }
            `}</style>
            <div className="sahaj-privacy" style={{ minHeight: '100vh' }}>
                <Header />

                {/* Hero */}
                <div style={{ background: '#111111', padding: '60px 0', marginTop: 80 }}>
                    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
                        <h1 className="sahaj-privacy-heading" style={{
                            fontSize: 48, fontWeight: 700, color: '#FFFFFF', margin: 0, letterSpacing: '-1px'
                        }}>
                            PRIVACY POLICY
                        </h1>
                        <p style={{ fontSize: 14, color: '#9CA3AF', marginTop: 12 }}>
                            Last updated: March 2026
                        </p>
                    </div>
                </div>

                <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 80px' }}>
                    {/* Commitment Banner */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 20,
                        padding: 28, background: '#E8F5F1', border: '1px solid #0D7C66',
                        marginBottom: 48
                    }}>
                        <Shield size={32} color="#0D7C66" style={{ flexShrink: 0 }} />
                        <div>
                            <h3 className="sahaj-privacy-heading" style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>
                                Your Privacy Matters
                            </h3>
                            <p style={{ fontSize: 14, color: '#6C757D', margin: 0, lineHeight: 1.5 }}>
                                We are committed to protecting your personal information and being transparent about how we collect and use your data.
                            </p>
                        </div>
                    </div>

                    <p style={{ fontSize: 16, lineHeight: 1.8, color: '#6C757D', marginBottom: 48 }}>
                        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and make purchases. Please read this policy carefully.
                    </p>

                    {sections.map((section, idx) => (
                        <div key={idx} style={{
                            marginBottom: 40, paddingBottom: 40,
                            borderBottom: idx < sections.length - 1 ? '1px solid #DEE2E6' : 'none'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                                <span className="sahaj-privacy-heading" style={{
                                    fontSize: 14, fontWeight: 700, color: '#0D7C66',
                                    background: '#E8F5F1', padding: '4px 12px',
                                    flexShrink: 0, marginTop: 4
                                }}>
                                    {String(idx + 1).padStart(2, '0')}
                                </span>
                                <div>
                                    <h2 className="sahaj-privacy-heading" style={{
                                        fontSize: 22, fontWeight: 700, margin: '0 0 16px', letterSpacing: '-0.3px'
                                    }}>
                                        {section.title}
                                    </h2>
                                    {section.content && (
                                        <p style={{ fontSize: 15, lineHeight: 1.8, color: '#6C757D', margin: 0 }}>
                                            {section.content}
                                        </p>
                                    )}
                                    {section.items && (
                                        <ul style={{ margin: 0, padding: '0 0 0 0', listStyle: 'none' }}>
                                            {section.items.map((item, i) => (
                                                <li key={i} style={{
                                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                                    fontSize: 15, lineHeight: 1.6, color: '#6C757D',
                                                    marginBottom: 8
                                                }}>
                                                    <span style={{
                                                        width: 6, height: 6, background: '#0D7C66',
                                                        flexShrink: 0, marginTop: 8
                                                    }} />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    <div style={{
                        background: '#F8F9FA', padding: 32, border: '1px solid #DEE2E6', marginTop: 20
                    }}>
                        <p style={{ fontSize: 14, color: '#6C757D', margin: '0 0 12px', lineHeight: 1.6 }}>
                            If you have questions or concerns about our privacy practices, please reach out to us.
                        </p>
                        <span
                            onClick={() => navigate('/template25/contact')}
                            style={{ fontSize: 14, color: '#0D7C66', fontWeight: 600, cursor: 'pointer' }}
                        >
                            Contact Us →
                        </span>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default PrivacyPolicy25;
